import { useEffect, useState } from "react";
import api, { apiError, getToken, API } from "@/lib/api";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, Download } from "lucide-react";
import { PayoutsByPro } from "@/components/admin/PayoutsByPro";
import { PaymentsLedger } from "@/components/admin/PaymentsLedger";
import { FilterBar, inRange } from "@/components/admin/FilterBar";

export default function AdminPayments() {
    const [tab, setTab] = useState("received");
    const [iq, setIq] = useState("");
    const [idates, setIdates] = useState({ from: "", to: "" });
    const [payouts, setPayouts] = useState(null);
    const [invoices, setInvoices] = useState(null);
    const [visitFees, setVisitFees] = useState(null);

    const load = () => {
        api.get("/admin/payouts").then((r) => setPayouts(r.data)).catch(() => setPayouts([]));
        api.get("/admin/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
        api.get("/admin/visit-fees").then((r) => setVisitFees(r.data)).catch(() => setVisitFees([]));
    };
    useEffect(() => { load(); }, []);

    const markPaid = async (id) => {
        try { await api.patch(`/admin/payouts/${id}`); toast.success("Payout marked paid"); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    return (
        <div>
            <h1 className="font-heading text-2xl font-extrabold text-navy">Payments</h1>
            <p className="text-sm text-navy/55">Professional payouts & customer invoices</p>
            <div className="mt-4 inline-flex rounded-full bg-navy/5 p-1">
                {["received", "payouts", "invoices", "visit fees"].map((t) => (
                    <button key={t} data-testid={`ptab-${t.replace(/\s/g, "-")}`} onClick={() => setTab(t)} className={`rounded-full px-5 py-2 text-sm font-bold capitalize transition-colors ${tab === t ? "bg-orange text-white" : "text-navy/60"}`}>{t}</button>
                ))}
            </div>

            {tab === "received" && <PaymentsLedger />}
            {tab === "payouts" && <PayoutsByPro payouts={payouts} onMarkPaid={markPaid} onChanged={load} />}
            {tab === "invoices" && <FilterBar testid="invoice-filter" q={iq} setQ={setIq} placeholder="Search invoice, booking, customer…" dates={idates} setDates={setIdates} onClear={() => { setIq(""); setIdates({ from: "", to: "" }); }} />}

            {tab === "invoices" && (
                <div className="mt-4 overflow-hidden rounded-2xl border border-navy/10 bg-white">
                    {invoices === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                    <div className="overflow-x-auto homiie-scroll">
                        <table className="w-full text-sm">
                            <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                                <tr><th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Booking</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
                            </thead>
                            <tbody>
                                {invoices?.filter((inv) => inRange(inv.created_at, idates) && (!iq || `${inv.invoice_number} ${inv.booking_ref} ${inv.customer_name} ${inv.service_name}`.toLowerCase().includes(iq.toLowerCase()))).map((inv) => (
                                    <tr key={inv.id} className="border-t border-navy/5" data-testid={`invoice-row-${inv.invoice_number}`}>
                                        <td className="px-4 py-3 font-heading font-bold text-navy">{inv.invoice_number}</td>
                                        <td className="px-4 py-3 text-navy/70">{inv.booking_ref}</td>
                                        <td className="px-4 py-3 text-navy/70">{inv.customer_name}</td>
                                        <td className="px-4 py-3 text-navy/70">{inv.service_name}</td>
                                        <td className="px-4 py-3 font-bold text-navy">{rupee(inv.total)}</td>
                                        <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${["CANCELLED", "REFUNDED"].includes(inv.payment_status) ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{inv.payment_status}</span></td>
                                        <td className="px-4 py-3"><a href={`${API}/bookings/${inv.booking_id}/invoice.pdf?auth=${getToken()}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-orange-dark"><Download size={14} /> PDF</a></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {invoices && invoices.length === 0 && <div className="py-12 text-center text-sm text-navy/40">No invoices yet.</div>}
                    </div>
                </div>
            )}

            {tab === "visit fees" && (
                <div className="mt-4 overflow-hidden rounded-2xl border border-navy/10 bg-white">
                    {visitFees === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                    <div className="overflow-x-auto homiie-scroll">
                        <table className="w-full text-sm">
                            <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                                <tr><th className="px-4 py-3">Order</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Services opted</th><th className="px-4 py-3">Method</th><th className="px-4 py-3">Visit fee</th><th className="px-4 py-3">Status</th></tr>
                            </thead>
                            <tbody>
                                {visitFees?.map((v) => (
                                    <tr key={v.id} className="border-t border-navy/5 align-top" data-testid={`visitfee-row-${v.order_ref}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-heading font-bold text-navy">{v.order_ref}</div>
                                            <div className="text-xs text-navy/45">{v.service_count} service{v.service_count > 1 ? "s" : ""}{v.preferred_date ? ` · ${v.preferred_date}` : ""}</div>
                                        </td>
                                        <td className="px-4 py-3 text-navy/80">{v.customer_name}<div className="text-xs text-navy/45">{v.customer_phone}{v.area_name ? ` · ${v.area_name}` : ""}</div></td>
                                        <td className="px-4 py-3">
                                            <div className="space-y-1">
                                                {v.services.map((s, i) => (
                                                    <div key={i} className="flex items-center gap-2 text-xs">
                                                        <span className="font-semibold text-navy">{s.name}</span>
                                                        <span className="text-navy/40">{s.booking_ref}</span>
                                                        <span className="text-navy/60">{rupee(s.visit_fee)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-navy/70">{v.method}</td>
                                        <td className="px-4 py-3 font-bold text-navy">{rupee(v.visit_fee_total)}</td>
                                        <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${v.status === "Paid" ? "bg-emerald-50 text-emerald-700" : v.status === "Partial" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{v.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {visitFees && visitFees.length === 0 && <div className="py-12 text-center text-sm text-navy/40">No visit-fee payments yet.</div>}
                    </div>
                </div>
            )}
        </div>
    );
}
